import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";

import config from "@/config";
import useCache from "@/hooks/useCache";
import useStateHandler from "@/hooks/useStateHandler";
import fetchWrapper from "@/services/fetchWrapper.service";
import { createSuarteworldViewSource } from "@/shared/lib/artworkViewSource";
import Loader from "@/shared-components/loader/Loader"; // ✅ Updated import
import StaticSeoHead from "@/shared-components/seo/StaticSeoHead";
import { getSsrDiscoverBootstrap } from "@/utils/ssrDiscoverBootstrap";

function readInitialSuarteworldSeoPayload() {
  const payload = getSsrDiscoverBootstrap('suarteworld');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

export default function SuarteWorld() {
  const suarteworldSeed = useMemo(() => parseInt(Math.random() * 1000000, 10), []);
  const suarteworldEndpoint = useMemo(
    () => `${config.apis.api.url}/suarteworld?seed=${suarteworldSeed}`,
    [suarteworldSeed]
  );
  const cacheEndpoint = useMemo(
    () => `/suarteworld?seed=${suarteworldSeed}`,
    [suarteworldSeed]
  );
  const { loading, fetchData } = useCache("suarteworld", suarteworldEndpoint, { expiresIn: ["0", "seconds"] });
  const { cacheHandler } = useStateHandler();
  const location = useLocation();
  const navigate = useNavigate();
  const { onSuarteworldReady } = useOutletContext() ?? {};
  const [suarteworldSeo, setSuarteworldSeo] = useState(() => readInitialSuarteworldSeoPayload());

  useEffect(() => {
    let ignore = false;

    async function loadSuarteworldSeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/discover/suarteworld`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setSuarteworldSeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load suarteworld SEO payload', error);
        }
      }
    }

    loadSuarteworldSeo();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!loading && Array.isArray(fetchData?.data) && fetchData.data.length > 0) {
      cacheHandler.storeInCache("artworks_of_suarteworld", { ...fetchData, endpoint: cacheEndpoint }, {
        invalidateWhen: ["EDIT_ARTWORK", "DELETE_ARTWORK"]
      });

      const firstArtwork = fetchData.data[0];
      const firstArtworkRouteId = firstArtwork?.slug || firstArtwork?._id;
      const firstImageId = firstArtwork?.artwork_media?.artwork_main_picture?.image_id;
      let hasNavigated = false;
      let fallbackTimer;

      const navigateToFirstArtwork = () => {
        if (hasNavigated) {
          return;
        }

        hasNavigated = true;
        clearTimeout(fallbackTimer);
        navigate(`/suarteworld/artwork/${firstArtworkRouteId}`, {
          state: {
            cacheKey: "artworks_of_suarteworld",
            from: location.state?.from,
            artworkViewSource: createSuarteworldViewSource(),
          },
          replace: true
        });

        window.requestAnimationFrame(() => {
          onSuarteworldReady?.();
        });
      };

      if (!firstImageId) {
        navigateToFirstArtwork();
        return undefined;
      }

      const preloadedImage = new Image();
      const viewportImageWidth = Math.min(
        2200,
        Math.max(200, Math.ceil((window.innerWidth * window.devicePixelRatio) / 200) * 200)
      );
      preloadedImage.onload = navigateToFirstArtwork;
      preloadedImage.onerror = navigateToFirstArtwork;
      preloadedImage.src = `${config.app.imageServiceDomain}/${firstImageId}/w=${viewportImageWidth}`;
      fallbackTimer = window.setTimeout(navigateToFirstArtwork, 2600);

      return () => {
        clearTimeout(fallbackTimer);
        preloadedImage.onload = null;
        preloadedImage.onerror = null;
      };
    }
    return undefined;
  }, [cacheEndpoint, cacheHandler, fetchData, loading, location.state?.from, navigate, onSuarteworldReady]);

  return (
    <>
      <StaticSeoHead
        title={suarteworldSeo?.title || "Suarteworld | Scroll and Interact with Fine Art"}
        description={suarteworldSeo?.metaDescription || "Explore Suarteworld, an immersive way to discover original artworks on Suarte. Scroll through the art world and interact with contemporary paintings from artists and galleries around the world."}
        canonicalUrl={suarteworldSeo?.canonicalUrl}
        canonicalPath={suarteworldSeo?.canonicalPath || "/suarteworld"}
        robots={suarteworldSeo?.robots}
        ogType={suarteworldSeo?.ogType}
        image={suarteworldSeo?.ogImage}
        pageType="CollectionPage"
        structuredData={suarteworldSeo?.structuredData}
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Suarteworld', url: 'https://suarte.art/suarteworld' },
        ]}
      />
      {loading && <Loader />} {/* ✅ Use the generic loader */}
    </>
  );
}
